import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import twilioDevice from "@/lib/twilioDevice";
import { labelFor, PURPOSES } from "@/lib/campaignMeta";
import PredictiveRun from "@/pages/PredictiveRun";
import { Phone, SkipForward, CheckCircle, User, Buildings, Lightbulb, ChatCircleDots, ClockCounterClockwise, ArrowLeft } from "@phosphor-icons/react";

const OUTCOMES = [
    { v: "connected|interested", r: "connected", d: "interested", label: "Connected — Interested" },
    { v: "connected|converted", r: "connected", d: "converted", label: "Connected — Converted" },
    { v: "connected|callback", r: "connected", d: "callback", label: "Connected — Callback Requested" },
    { v: "connected|not_interested", r: "connected", d: "not_interested", label: "Connected — Not Interested" },
    { v: "no_answer|", r: "no_answer", d: "", label: "No Answer" },
    { v: "busy|", r: "busy", d: "", label: "Busy" },
    { v: "voicemail|", r: "voicemail", d: "", label: "Voicemail" },
];

export default function CampaignRun() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [campaignMeta, setCampaignMeta] = useState(null);
    const [metaLoading, setMetaLoading] = useState(true);
    const [outcome, setOutcome] = useState("");
    const [callbackAt, setCallbackAt] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    const fetchNext = useCallback(async () => {
        setLoading(true);
        setOutcome("");
        setNotes("");
        try {
            const { data: d } = await api.get(`/campaigns/${id}/next-record`);
            setData(d);
            if (d.record && d.dialing_mode === "power") twilioDevice.requestDial(d.record.phone, { autoDial: true, campaign: true });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load campaign");
        } finally {
            setLoading(false);
        }
    }, [id]);

    const initRef = useRef(false);
    useEffect(() => {
        let mounted = true;
        api.get(`/campaigns/${id}/meta`).then(({ data: c }) => {
            if (!mounted) return;
            setCampaignMeta(c);
            setMetaLoading(false);
            if (c.dialing_mode !== "predictive" && !initRef.current) { initRef.current = true; fetchNext(); }
        }).catch(() => {
            if (!mounted) return;
            setMetaLoading(false);
            if (!initRef.current) { initRef.current = true; fetchNext(); }
        });
        return () => { mounted = false; };
    }, [id, fetchNext]);

    if (metaLoading) return <AppShell><div className="mx-auto max-w-5xl px-6 py-16 text-stone2">Loading workspace…</div></AppShell>;
    if (campaignMeta?.dialing_mode === "predictive") return <PredictiveRun campaignId={id} campaign={campaignMeta} />;

    const rec = data?.record;
    const sf = data?.salesforce || {};

    const call = () => { if (rec) twilioDevice.requestDial(rec.phone, { autoDial: true, campaign: true }); };

    const saveNext = async () => {
        if (!outcome) { toast.error("Select the call outcome first"); return; }
        const o = OUTCOMES.find((x) => x.v === outcome);
        if (o.d === "callback" && !callbackAt) { toast.error("Pick a callback date & time"); return; }
        setSaving(true);
        try {
            const payload = { result: o.r, disposition: o.d || null, notes };
            if (o.d === "callback" && callbackAt) payload.callback_at = new Date(callbackAt).toISOString();
            const res = await api.post(`/campaigns/${id}/records/${rec.id}/disposition`, payload);
            toast.success(res.data?.outcome || "Saved");
            setOutcome(""); setCallbackAt(""); setNotes("");
            fetchNext();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const skip = async () => {
        try { await api.post(`/campaigns/${id}/records/${rec.id}/skip`); } catch { /* ignore */ }
        fetchNext();
    };

    if (loading) return <AppShell><div className="mx-auto max-w-5xl px-6 py-16 text-stone2">Loading workspace…</div></AppShell>;

    if (!rec) return (
        <AppShell>
            <div className="mx-auto max-w-2xl px-6 py-20 text-center" data-testid="campaign-run-done">
                <CheckCircle size={40} weight="fill" className="mx-auto text-emerald-500" />
                <h1 className="mt-4 font-heading text-3xl font-bold">All caught up!</h1>
                <p className="mt-2 text-stone2">There are no more records to dial in this campaign right now.</p>
                <button onClick={() => navigate(`/campaigns/${id}`)} className="mt-6 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white">Back to dashboard</button>
            </div>
        </AppShell>
    );

    return (
        <AppShell onCallCompleted={() => {}}>
            <div className="mx-auto max-w-6xl px-6 py-8 md:px-12 md:py-10" data-testid="campaign-run-page">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <button onClick={() => navigate(`/campaigns/${id}`)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-stone2 hover:text-ink"><ArrowLeft size={15} /> {data.campaign_name}</button>
                        <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Agent Workspace</h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="rounded-full bg-stone-100 px-3 py-1.5 text-xs font-semibold text-stone2">{labelFor(PURPOSES, data.purpose)} · {data.dialing_mode} dialer</span>
                        <span data-testid="run-remaining" className="rounded-full bg-brand/10 px-3 py-1.5 text-xs font-bold text-brand">{data.remaining} remaining</span>
                    </div>
                </div>

                <div className="mt-6 grid gap-5 lg:grid-cols-5">
                    {/* Left: customer + SF + history */}
                    <div className="space-y-5 lg:col-span-2">
                        <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="run-customer">
                            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2"><User size={14} weight="fill" /> Customer</div>
                            <div className="mt-2 font-heading text-xl font-bold">{rec.name || "Unknown contact"}</div>
                            <div className="mt-1 font-mono text-sm text-ink">{rec.phone}</div>
                            {rec.company && <div className="mt-1 flex items-center gap-1.5 text-sm text-stone2"><Buildings size={14} /> {rec.company}</div>}
                            {rec.email && <div className="text-sm text-stone2">{rec.email}</div>}
                            <button onClick={call} data-testid="run-call-btn" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-brand py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5">
                                <Phone size={16} weight="fill" /> Call {rec.name?.split(" ")[0] || "now"}
                            </button>
                            <p className="mt-2 text-center text-[11px] text-stone2">Places the call from your browser — make sure your mic is allowed.</p>
                        </div>

                        <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="run-salesforce">
                            <div className="text-[11px] font-bold uppercase tracking-widest text-stone2">Salesforce</div>
                            {sf.matched ? (
                                <div className="mt-2 text-sm">
                                    <div className="font-semibold text-ink">{sf.party?.name} <span className="text-xs font-normal text-stone2">({sf.party?.type})</span></div>
                                    {Object.entries(sf.records || {}).map(([k, v]) => (
                                        <div key={k} className="mt-1 text-stone2">{k}: <span className="font-semibold text-ink">{v.length}</span></div>
                                    ))}
                                </div>
                            ) : <p className="mt-2 text-sm text-stone2">No matching Salesforce record for this number.</p>}
                        </div>

                        <div className="rounded-2xl border border-stone-200 bg-white p-5">
                            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2"><ClockCounterClockwise size={14} /> Previous Calls</div>
                            {data.previous_calls?.length ? (
                                <div className="mt-2 space-y-2">
                                    {data.previous_calls.map((c, i) => (
                                        <div key={i} className="rounded-lg bg-stone-50 px-3 py-2 text-xs">
                                            <span className="font-semibold text-ink">{c.direction}</span> · {c.status} {c.disposition ? `· ${c.disposition}` : ""}
                                            {c.notes && <div className="mt-0.5 text-stone2">{c.notes}</div>}
                                        </div>
                                    ))}
                                </div>
                            ) : <p className="mt-2 text-sm text-stone2">No prior calls to this number.</p>}
                        </div>
                    </div>

                    {/* Right: AI help + disposition */}
                    <div className="space-y-5 lg:col-span-3">
                        <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-5" data-testid="run-talking-points">
                            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-indigo-700"><Lightbulb size={14} weight="fill" /> AI Suggested Talking Points</div>
                            <ul className="mt-2 space-y-1.5 text-sm text-ink">
                                {data.talking_points?.map((p, i) => <li key={i} className="flex gap-2"><span className="text-indigo-400">•</span> {p}</li>)}
                            </ul>
                        </div>
                        <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="run-objections">
                            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2"><ChatCircleDots size={14} weight="fill" /> Objection Handling</div>
                            <div className="mt-2 space-y-2">
                                {data.objections?.map((o, i) => (
                                    <div key={i} className="rounded-lg border border-stone-100 bg-stone-50 px-3 py-2 text-sm">
                                        <div className="font-semibold text-ink">“{o.objection}”</div>
                                        <div className="mt-0.5 text-stone2">{o.response}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="rounded-2xl border border-stone-200 bg-white p-5">
                            <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Notes</label>
                            <textarea data-testid="run-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="What happened on this call?" className="mt-1.5 w-full resize-none rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-ink" />
                            <label className="mt-3 block text-[11px] font-bold uppercase tracking-widest text-stone2">Outcome / Disposition</label>
                            <select data-testid="run-outcome" value={outcome} onChange={(e) => setOutcome(e.target.value)} className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-ink">
                                <option value="">Select outcome…</option>
                                {OUTCOMES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                            </select>
                            {OUTCOMES.find((x) => x.v === outcome)?.d === "callback" && (
                                <div className="mt-3">
                                    <label className="block text-[11px] font-bold uppercase tracking-widest text-stone2">Callback date &amp; time</label>
                                    <input type="datetime-local" data-testid="run-callback-at" value={callbackAt} onChange={(e) => setCallbackAt(e.target.value)}
                                        className="mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-ink" />
                                    <p className="mt-1 text-xs text-stone2">The lead will reappear in the dialer at this time, held for you first.</p>
                                </div>
                            )}
                            <div className="mt-4 flex gap-2">
                                <button onClick={saveNext} disabled={saving} data-testid="run-save-next" className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-ink py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50">
                                    <CheckCircle size={16} weight="fill" /> Save & Next
                                </button>
                                <button onClick={skip} data-testid="run-skip" className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-5 py-3 text-sm font-semibold text-stone2 hover:border-ink">
                                    <SkipForward size={15} /> Skip
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
